import React, { createContext, useContext, useState, useEffect } from 'react';
import ApiManager from '../services/api/ApiManager';

const DataContext = createContext();

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export const DataProvider = ({ children }) => {
  const [freightData, setFreightData] = useState([]);
  const [lanePricingData, setLanePricingData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dataSource, setDataSource] = useState('loading'); // 'api', 'mock', 'pending'
  const [hasLoadedData, setHasLoadedData] = useState(false);

  // Monitor for token availability and trigger data load
  useEffect(() => {
    console.log('🔍 DataContext - Skipping all data loading during login for speed');
    
    // Skip ALL data loading to speed up login - just set empty data
    if (!hasLoadedData) {
      setFreightData([]);
      setLanePricingData([]);
      setDataSource('deferred-until-needed');
      setLoading(false);
      setHasLoadedData(true);
      console.log('✅ Data context initialized with empty data - login should be instant');
    }
    return;
    
    // If we've already loaded data, don't reload
    if (hasLoadedData) return;
    
    // Check if we have a valid access token
    const token = localStorage.getItem('access_token');
    
    // If we're in the middle of Auth0 redirect (code in URL), wait a bit
    const urlParams = new URLSearchParams(window.location.search);
    const isAuthRedirect = urlParams.has('code') || urlParams.has('state');
    
    if (isAuthRedirect) {
      console.log('🔄 Auth0 redirect detected, will retry data load shortly...');
      setLoading(false);
      setDataSource('pending');
      
      // Try again in 2 seconds when auth should be complete
      const retryTimeout = setTimeout(() => {
        const newToken = localStorage.getItem('access_token');
        if (newToken && !hasLoadedData) {
          console.log('✅ Token available after redirect, loading data...');
          loadInitialData();
        }
      }, 2000);
      
      return () => clearTimeout(retryTimeout);
    }
    
    // If we have a token, load immediately
    if (token) {
      loadInitialData();
    } else {
      console.log('⏳ No access token yet, setting up retry...');
      // Set up interval to check for token
      const checkInterval = setInterval(() => {
        const newToken = localStorage.getItem('access_token');
        if (newToken && !hasLoadedData) {
          console.log('✅ Token detected, loading data...');
          clearInterval(checkInterval);
          loadInitialData();
        }
      }, 1000);
      
      // Give up after 10 seconds and use mock data
      const giveUpTimeout = setTimeout(() => {
        clearInterval(checkInterval);
        if (!hasLoadedData) {
          console.log('🎭 No token after 10s, using mock data...');
          const mockData = generateMockFreightData();
          setFreightData(mockData);
          setLanePricingData(transformLanePricingData(mockData));
          setDataSource('mock');
          setLoading(false);
          setHasLoadedData(true);
        }
      }, 10000);
      
      return () => {
        clearInterval(checkInterval);
        clearTimeout(giveUpTimeout);
      };
    }
  }, [hasLoadedData]);

  const loadInitialData = async () => {
    console.log('🚀 Starting initial data load from unified API manager...');
    setLoading(true);
    setError(null);

    try {
      // First test API connectivity using unified ApiManager
      console.log('📝 Testing backend API connectivity...');
      const analyticsService = ApiManager.getService('analytics');
      const connectivityResult = await analyticsService.testConnectivity();
      
      if (connectivityResult.isOnline) {
        console.log('✅ Backend API is online, fetching data...');
        
        // Load freight data from backend API using unified ApiManager
        const rawData = await ApiManager.getFreightData({ limit: 1000 });
        console.log(`📊 Loaded ${rawData?.length || 0} records from unified API manager`);
        
        if (rawData && rawData.length > 0) {
          console.log(`✅ Successfully loaded ${rawData.length} records from unified API manager`);
          
          // The backend already returns transformed data
          setFreightData(rawData);
          console.log('✅ Freight data set successfully');
          
          // Skip lane pricing transformation for now to speed up login
          console.log('⏭️ Skipping lane pricing transformation to speed up login');
          setLanePricingData([]);
          console.log('✅ Lane pricing data set to empty array');
          
          setDataSource('api');
          console.log('✅ Data source set to api');
          setLoading(false);
          console.log('✅ Loading set to false - login should complete now!');
          return;
        }
      }
    } catch (error) {
      console.warn('⚠️ Failed to load from unified API manager:', error.message);
    }

    // Fallback to mock data if API fails
    console.log('🎭 API failed, falling back to mock data...');
    const mockData = generateMockFreightData();
    setFreightData(mockData);
    setLanePricingData(transformLanePricingData(mockData));
    setDataSource('mock');
    setError('Backend API unavailable, using mock data');
    setLoading(false);
  };

  const transformLanePricingData = (rawData) => {
    console.log('🔄 Transforming lane pricing data...');
    
    // Group by origin-destination pairs
    const laneMap = new Map();
    
    rawData.forEach(record => {
      if (!record.OriginCity || !record.DestinationCity || !record.RatePerMile) {
        return;
      }
      
      const laneKey = `${record.OriginCity}, ${record.OriginState} → ${record.DestinationCity}, ${record.DestinationState}`;
      
      if (!laneMap.has(laneKey)) {
        laneMap.set(laneKey, {
          lane: laneKey,
          originCity: record.OriginCity,
          originState: record.OriginState,
          destinationCity: record.DestinationCity,
          destinationState: record.DestinationState,
          rates: [],
          payments: [],
          mileages: [],
          equipment: new Set(),
          brokers: new Set(),
          pickupDates: []
        });
      }
      
      const laneData = laneMap.get(laneKey);
      laneData.rates.push(parseFloat(record.RatePerMile) || 0);
      laneData.payments.push(parseFloat(record.PaymentAmount) || 0);
      laneData.mileages.push(parseFloat(record.Mileage) || 0);
      laneData.equipment.add(record.Equipment);
      laneData.brokers.add(record.BrokerName);
      laneData.pickupDates.push(record.PickupDate);
    });
    
    // Transform to lane pricing format
    const lanePricingData = Array.from(laneMap.values()).map(lane => {
      const avgRate = lane.rates.length > 0 
        ? lane.rates.reduce((sum, rate) => sum + rate, 0) / lane.rates.length 
        : 0;
      const avgPayment = lane.payments.length > 0 
        ? lane.payments.reduce((sum, payment) => sum + payment, 0) / lane.payments.length 
        : 0;
      const avgMileage = lane.mileages.length > 0 
        ? lane.mileages.reduce((sum, mileage) => sum + mileage, 0) / lane.mileages.length 
        : 0;
      
      return {
        ...lane,
        loadCount: lane.rates.length,
        averageRate: Math.round(avgRate * 100) / 100,
        averagePayment: Math.round(avgPayment),
        averageMileage: Math.round(avgMileage),
        equipmentTypes: Array.from(lane.equipment).join(', '),
        brokerCount: lane.brokers.size,
        equipment: undefined, // Remove Set object
        brokers: undefined    // Remove Set object
      };
    }).filter(lane => lane.loadCount > 0);
    
    // Sort by load count descending
    lanePricingData.sort((a, b) => b.loadCount - a.loadCount);
    
    console.log(`📈 Generated ${lanePricingData.length} lane pricing records`);
    return lanePricingData;
  };

  const generateMockFreightData = () => {
    console.log('🎭 Generating mock freight data...');
    
    const origins = [
      { city: 'Chicago', state: 'IL', lat: 41.8781, lon: -87.6298 },
      { city: 'Detroit', state: 'MI', lat: 42.3314, lon: -83.0458 },
      { city: 'Milwaukee', state: 'WI', lat: 43.0389, lon: -87.9065 },
      { city: 'Indianapolis', state: 'IN', lat: 39.7684, lon: -86.1581 },
      { city: 'Kansas City', state: 'MO', lat: 39.0997, lon: -94.5786 },
      { city: 'Memphis', state: 'TN', lat: 35.1495, lon: -90.0490 }
    ];
    
    const destinations = [
      { city: 'Los Angeles', state: 'CA', lat: 34.0522, lon: -118.2437 },
      { city: 'Phoenix', state: 'AZ', lat: 33.4484, lon: -112.0740 },
      { city: 'Denver', state: 'CO', lat: 39.7392, lon: -104.9903 },
      { city: 'Dallas', state: 'TX', lat: 32.7767, lon: -96.7970 },
      { city: 'Atlanta', state: 'GA', lat: 33.7490, lon: -84.3880 },
      { city: 'Miami', state: 'FL', lat: 25.7617, lon: -80.1918 },
      { city: 'Seattle', state: 'WA', lat: 47.6062, lon: -122.3321 },
      { city: 'Portland', state: 'OR', lat: 45.5152, lon: -122.6784 }
    ];
    
    const equipmentTypes = ['F', 'FA', 'FSD', 'V', 'R', 'HS', 'CONG', 'DD'];
    const brokers = [
      'ABC Logistics', 'FreightCorp', 'LoadMaster', 'TransPort Inc', 
      'ShipRight', 'CargoConnect', 'HaulFast', 'RoadRunner Freight'
    ];

    const mockData = [];
    
    for (let i = 0; i < 100; i++) {
      const origin = origins[Math.floor(Math.random() * origins.length)];
      const destination = destinations[Math.floor(Math.random() * destinations.length)];
      
      // Calculate approximate distance
      const distance = Math.round(200 + Math.random() * 2000);
      const baseRate = 2.00 + Math.random() * 2.00;
      const payment = Math.round(distance * baseRate);
      
      mockData.push({
        ID: `MOCK${1000 + i}`,
        Origin: `${origin.city}, ${origin.state}`,
        Destination: `${destination.city}, ${destination.state}`,
        OriginCity: origin.city,
        OriginState: origin.state,
        DestinationCity: destination.city,
        DestinationState: destination.state,
        oLat: origin.lat,
        oLon: origin.lon,
        dLat: destination.lat,
        dLon: destination.lon,
        PaymentAmount: payment,
        RatePerMile: Math.round(baseRate * 100) / 100,
        RatePerMileWithDH: Math.round((baseRate + 0.15) * 100) / 100,
        Equipment: equipmentTypes[Math.floor(Math.random() * equipmentTypes.length)],
        Mileage: distance,
        Length: Math.floor(Math.random() * 53) + 20,
        Weight: Math.floor(Math.random() * 40000) + 10000,
        PickupDate: new Date(Date.now() + Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        BrokerName: brokers[Math.floor(Math.random() * brokers.length)],
        BrokerEmail: `contact@${brokers[Math.floor(Math.random() * brokers.length)].toLowerCase().replace(/\s+/g, '')}.com`,
        BrokerCity: origin.city,
        BrokerState: origin.state,
        PointOfContact: `Contact ${i + 1}`,
        PointOfContactPhone: `555-${String(Math.floor(Math.random() * 900) + 100)}-${String(Math.floor(Math.random() * 9000) + 1000)}`,
        MCNumber: `MC${100000 + Math.floor(Math.random() * 900000)}`,
        SpecInfo: Math.random() > 0.8 ? 'Hazmat' : 'Standard',
        LoadType: 'Full',
        HaulType: distance < 250 ? 'Short Haul' : distance < 750 ? 'Medium Haul' : 'Long Haul',
        OriginDistance: Math.floor(Math.random() * 150) + 10,
        OperatingProfit: Math.round(payment * (0.1 + Math.random() * 0.2))
      });
    }

    return mockData;
  };

  const refreshData = async () => {
    await loadInitialData();
  };

  const filterDataByDate = (startDate, endDate) => {
    return freightData.filter(record => {
      const pickupDate = new Date(record.PickupDate);
      return pickupDate >= new Date(startDate) && pickupDate <= new Date(endDate);
    });
  };

  const filterDataByOriginState = (states) => {
    if (!states || states.length === 0) return freightData;
    return freightData.filter(record => states.includes(record.OriginState));
  };

  const filterDataByDestinationState = (states) => {
    if (!states || states.length === 0) return freightData;
    return freightData.filter(record => states.includes(record.DestinationState));
  };

  const getDataStats = () => {
    return {
      totalRecords: freightData.length,
      dataSource,
      uniqueOriginStates: [...new Set(freightData.map(r => r.OriginState))].length,
      uniqueDestinationStates: [...new Set(freightData.map(r => r.DestinationState))].length,
      totalLanes: lanePricingData.length,
      avgRatePerMile: freightData.length > 0 
        ? freightData.reduce((sum, r) => sum + (parseFloat(r.RatePerMile) || 0), 0) / freightData.length 
        : 0
    };
  };

  const value = {
    // Data
    freightData,
    lanePricingData,
    
    // Status
    loading,
    error,
    dataSource,
    
    // Actions
    refreshData,
    
    // Filters
    filterDataByDate,
    filterDataByOriginState,
    filterDataByDestinationState,
    
    // Utils
    getDataStats
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};